import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Store, ArrowRight, Loader2 } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const { user, loading, loginPassword } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/admin", { replace: true });
  }, [user, loading, navigate]);

  const handleLogin = () => {
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!username || !password) { toast.error("Enter username and password"); return; }
    setSubmitting(true);
    try {
      await loginPassword(username, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid username or password");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-[#0a0a0a] text-white p-12">
        <Link to="/" className="font-heading font-bold text-2xl tracking-tight" data-testid="login-brand">
          STORE<span className="casper-text">IG</span>
        </Link>
        <div>
          <h1 className="font-heading font-light text-5xl leading-tight tracking-tight">
            Run every<br />Instagram store<br />from one place.
          </h1>
          <p className="font-body text-zinc-400 mt-6 max-w-md">
            One admin. Multiple storefronts. Real Stripe checkout, live analytics, and an
            Instagram-native shopping experience your customers already know.
          </p>
        </div>
        <p className="text-xs text-zinc-500 tracking-[0.2em] uppercase">Merchant Dashboard</p>
      </div>

      <div className="flex items-center justify-center p-8 bg-[#fafafa]">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="lg:hidden mb-8 font-heading font-bold text-3xl text-center">
            STORE<span className="casper-text">IG</span>
          </div>
          <span className="text-xs font-heading font-bold uppercase tracking-[0.2em] text-zinc-500">
            Welcome back
          </span>
          <h2 className="font-heading font-medium text-3xl tracking-tight mt-2 mb-2">Sign in</h2>
          <p className="font-body text-zinc-500 mb-6">Use your username & password, or continue with Google.</p>

          <form onSubmit={handlePasswordLogin} className="space-y-3">
            <input
              data-testid="login-username-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Username or email"
              autoComplete="username"
              className="w-full border border-zinc-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-black bg-white"
            />
            <input
              data-testid="login-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              autoComplete="current-password"
              className="w-full border border-zinc-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-black bg-white"
            />
            <button
              type="submit"
              data-testid="password-login-button"
              disabled={submitting}
              className="w-full flex items-center justify-center gap-2 casper-btn rounded-full py-3.5 font-body font-semibold disabled:opacity-60"
            >
              {submitting && <Loader2 size={16} className="animate-spin" />} Sign in
            </button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-zinc-200" />
            <span className="text-xs text-zinc-400 uppercase tracking-wider">or</span>
            <div className="flex-1 h-px bg-zinc-200" />
          </div>

          <button
            data-testid="google-login-button"
            onClick={handleLogin}
            className="w-full flex items-center justify-center gap-3 bg-white border border-zinc-200 rounded-full py-3.5 font-body font-semibold hover:bg-black hover:text-white hover:border-black transition-colors duration-200 active:scale-95"
          >
            <svg width="20" height="20" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
            Continue with Google
          </button>
          <Link to="/" className="mt-6 flex items-center justify-center gap-1 text-sm text-zinc-500 hover:text-black transition-colors" data-testid="back-to-store-link">
            <Store size={14} /> Browse stores <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}
